import { Injectable, NgZone } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';
import { DictionaryStr } from './models';
import { CookieOptions, CookieService } from './cookie/ngx-cookie/src/public-api';
import dayjs from 'dayjs';
import { SsrService } from './ssr.service';
import { LogginService } from './logging/logging-provider';

export enum AnonymousUserGuid {
  Anonymous = 'Anonymous',
}

@Injectable({
  providedIn: 'root',
})
export class LocalDataStorageService {
  private supportedUiCultures: DictionaryStr<string> = {
    ka: 'ka-GE',
    en: 'en-US',
    ru: 'ru-RU',
  };

  private localStorageMemoryFallBack = new Map();

  private store(key: string, value: any) {
    if (this.ssr.isPlatformBrowser()) {
      this.storage.store(key, value);
    } else {
      this.localStorageMemoryFallBack.set(key, value);
    }
  }

  private retrieve(key: string) {
    if (this.ssr.isPlatformBrowser()) {
      return this.storage.retrieve(key);
    } else {
      return this.localStorageMemoryFallBack.get(key);
    }
  }

  private clear(key: string) {
    if (this.ssr.isPlatformBrowser()) {
      this.storage.clear(key);
    } else {
      this.localStorageMemoryFallBack.delete(key);
    }
  }

  private put(key: string, value: string, options?: CookieOptions) {
    this.cookie.put(key, value, options);
  }

  private get(key: string) {
    return this.cookie.get(key);
  }

  constructor(
    private storage: LocalStorageService,
    private cookie: CookieService,
    private ssr: SsrService,
    private logService: LogginService,
    private ngZone: NgZone
  ) {
    this.listenToStorageEvents();
  }

  private listenToStorageEvents() {
    if (!this.ssr.isPlatformBrowser()) return;

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === `ngx-webstorage|${DataStorageKey.UserSessionEvent}`) {
        const action = event.newValue;

        this.ngZone.run(() => {
          if (JSON.parse(action) === UserSessionEnum.Login) {
            location.reload();
          } else if (JSON.parse(action) === UserSessionEnum.LogOut) {
            location.reload();
          }
        });
      }
    });
  }

  public storeAccessToken(accessToken: string, isLoginEvent: boolean) {
    this.store(DataStorageKey.AccessToken, accessToken);

    if (isLoginEvent) {
      this.broadcastSessionChange(UserSessionEnum.Login);
    }
  }

  public storeAnonymousGuid(uniqueGuide: string) {
    this.store(AnonymousUserGuid.Anonymous, uniqueGuide);
  }

  public getAnonymousGuid() {
    return this.retrieve(AnonymousUserGuid.Anonymous);
  }

  public getAccessToken(): string {
    return this.retrieve(DataStorageKey.AccessToken);
  }

  public destroyAccessToken(isLogoutEvent: boolean) {
    this.clear(DataStorageKey.AccessToken);
    
    if(isLogoutEvent){
      this.broadcastSessionChange(UserSessionEnum.LogOut)
    }
  }

  public hasAccessToken() {
    return (
      this.getAccessToken() !== null && this.getAccessToken() !== undefined
    );
  }

  public isUiLanguageSet() {
    return this.getUiLanguage() !== null && this.getUiLanguage() !== undefined;
  }

  public getUiLanguage(): string {
    return this.get(DataStorageKey.UiLanguage);
  }

  public setUiLanguage(language: string) {
    const date = dayjs(new Date()).add(1, 'y').toDate();
    return this.put(DataStorageKey.UiLanguage, language, {
      expires: date,
      path: '/',
    });
  }

  public getUiCulture() {
    const language = this.getUiLanguage();
    if (this.supportedUiCultures[language] == null) {
      throw Error('Unsupported language');
    }

    return this.supportedUiCultures[language];
  }

  private broadcastSessionChange(action: UserSessionEnum) {
    if (this.ssr.isPlatformBrowser()) {
      this.store(DataStorageKey.UserSessionEvent, action);

      setTimeout(() => {
        this.clear(DataStorageKey.UserSessionEvent);
      }, 100);
    }
  }
}

export enum DataStorageKey {
  AccessToken = 'core_accessToken',
  UiLanguage = 'culture',
  UserSessionEvent = 'user_session_event',
}

export enum UserSessionEnum {
  Login = 'login',
  LogOut = 'logout'
}
